// platform/src/components/mw/MwType.tsx
//
// Componentes de tipografía MW. Garantizan consistencia de tipos,
// pesos, line-heights y tracking en toda la landing.
//
// Tokens consumidos desde styles/mw.css (single source of truth).
// Cero hex hardcoded.

import type { ReactNode } from 'react'

type TextColor = 'ink' | 'secondary' | 'muted' | 'accent' | 'inverse' | 'inverseMuted'

const COLOR_MAP: Record<TextColor, string> = {
  ink: 'var(--mw-ink)',
  secondary: 'var(--mw-ink-secondary)',
  muted: 'var(--mw-ink-muted)',
  accent: 'var(--mw-accent)',
  inverse: 'var(--mw-ink-inverse)',
  inverseMuted: 'var(--mw-ink-inverse-muted)',
}

// ─── EYEBROW ────────────────────────────────────────────────
interface MwEyebrowProps {
  children: ReactNode
  color?: TextColor
  className?: string
}

export function MwEyebrow({ children, color = 'muted', className = '' }: MwEyebrowProps) {
  return (
    <p
      className={`text-[11px] font-medium uppercase ${className}`}
      style={{
        color: COLOR_MAP[color],
        letterSpacing: '0.12em',
        fontFamily: 'var(--mw-font-body)',
      }}
    >
      {children}
    </p>
  )
}

// ─── HEADING ────────────────────────────────────────────────
interface MwHeadingProps {
  children: ReactNode
  as?: 'h1' | 'h2' | 'h3'
  color?: TextColor
  className?: string
}

const HEADING_STYLES: Record<NonNullable<MwHeadingProps['as']>, React.CSSProperties> = {
  h1: {
    fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
    lineHeight: 1.02,
    letterSpacing: '-0.035em',
    fontWeight: 700,
  },
  h2: {
    fontSize: 'clamp(1.875rem, 4vw, 3rem)',
    lineHeight: 1.08,
    letterSpacing: '-0.025em',
    fontWeight: 700,
  },
  h3: {
    fontSize: '1.25rem',
    lineHeight: 1.25,
    letterSpacing: '-0.015em',
    fontWeight: 600,
  },
}

export function MwHeading({ children, as = 'h2', color = 'ink', className = '' }: MwHeadingProps) {
  const Tag = as
  return (
    <Tag
      className={className}
      style={{
        ...HEADING_STYLES[as],
        color: COLOR_MAP[color],
        fontFamily: 'var(--mw-font-display)',
      }}
    >
      {children}
    </Tag>
  )
}

// ─── TEXT ───────────────────────────────────────────────────
interface MwTextProps {
  children: ReactNode
  size?: 'lead' | 'body' | 'small'
  color?: TextColor
  className?: string
  as?: 'p' | 'span' | 'div'
}

const TEXT_STYLES: Record<NonNullable<MwTextProps['size']>, React.CSSProperties> = {
  lead: { fontSize: 'clamp(1.0625rem, 1.6vw, 1.25rem)', lineHeight: 1.55, fontWeight: 400 },
  body: { fontSize: '1rem', lineHeight: 1.6, fontWeight: 400 },
  small: { fontSize: '0.875rem', lineHeight: 1.5, fontWeight: 400 },
}

export function MwText({
  children,
  size = 'body',
  color = 'secondary',
  className = '',
  as: Tag = 'p',
}: MwTextProps) {
  return (
    <Tag
      className={className}
      style={{
        ...TEXT_STYLES[size],
        color: COLOR_MAP[color],
        fontFamily: 'var(--mw-font-body)',
      }}
    >
      {children}
    </Tag>
  )
}
